import { useEffect, useState, Suspense } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useParams,
  useNavigate,
} from 'react-router';
import { ThemeProvider } from '@/lib/components/theme-provider';
import { Toaster } from '@/components/ui/toaster';
import Loader from '@/lib/components/Loader';
import { useMutation } from '@tanstack/react-query';
import { SideBar } from '../SideBar';
import { toast } from '@/components/ui/use-toast';
import { glacierNewGradApiValidateAgencyMutation } from '@/lib/heyapi/@tanstack/react-query.gen';
import { NewGradPaidContext } from './NewGradPaidContext';
import type { UsStateEnum } from '@/lib/heyapi';

export type AgencyType = {
  name: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  email: string;
  agencyUuid?: string;
  agencyOperatingStates: UsStateEnum[];
};

export const NewGradLayout = () => {
  const params = useLocation();
  const { pk, agency_id } = useParams();
  const navigate = useNavigate();

  const [agencyData, setAgencyData] = useState<AgencyType>({
    name: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    agencyUuid: '',
    agencyOperatingStates: [],
  });

  const validateAgency = useMutation({
    ...glacierNewGradApiValidateAgencyMutation(),
    onError: (error) => {
      setAgencyData({
        name: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        agencyUuid: '',
        agencyOperatingStates: [],
      });

      toast({
        title: 'Error validating Agency',
        description: (
          <p className="mt-2 max-h-[40vh] w-[340px] rounded-md bg-red-600 p-4 text-white">
            {JSON.stringify(error)}
          </p>
        ),
      });
    },
    onSuccess: (_data) => {
      setAgencyData({
        name: _data.agencyName || '',
        firstName: _data.firstName || '',
        lastName: _data.lastName || '',
        phone: _data.phone || '',
        email: _data.email || '',
        agencyUuid: _data.agencyUuid || '',
        agencyOperatingStates: _data.agencyOperatingStates,
      });
    },
  });

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const FeatureFlag_NewGrad = import.meta.env.VITE_ENABLE_NEW_GRAD === 'true';

  useEffect(() => {
    if (!FeatureFlag_NewGrad) {
      navigate('/');
    }
  }, [FeatureFlag_NewGrad]);

  if (!FeatureFlag_NewGrad) {
    return <div></div>;
  }

  const [canNavigate, setCanNavigate] = useState(false);

  const pageNumber = !pk
    ? params.pathname.split('/')[2]
    : params.pathname.split('/')[3];
  return (
    <ThemeProvider>
      <NewGradPaidContext.Provider
        value={{
          canNavigate,
          setCanNavigate,
        }}
      >
        <div className="flex flex-col dark:bg-black dark:text-white">
          <div className="min-h-14 bg-brand-dark-green px-4 md:min-h-20 md:px-6">
            <Link
              to={`/newgrad${pk ? `/${pk}` : ''}/1/${agency_id || ''}`}
              key="Home"
              className="my-4 ml-[16px] flex items-center gap-4 md:flex-row md:gap-8"
            >
              <div className="flex flex-col">
                <img
                  src="/assets/aspen_sm.svg"
                  alt="Aspen logo"
                  className="w-16 md:w-24"
                />
              </div>
              <p className="mt-[9px] flex justify-center text-lg font-semibold text-white md:mt-4 md:w-full md:text-2xl">
                New Grad Online Portal
              </p>
            </Link>
          </div>
          <div className="flex flex-col">
            <main className="w-full max-w-full">
              <section className="wrapper mx-auto flex max-w-full items-center justify-between py-2">
                <div className="mt-[18px] flex w-full flex-col justify-center gap-8 md:flex-row">
                  <div className="flex w-full flex-col md:w-4/12 md:max-w-[350px]">
                    <SideBar
                      currentStep={Number(pageNumber)}
                      agencyData={agencyData}
                      canNavigate={canNavigate}
                      validateAgency={validateAgency}
                    />
                  </div>
                  <div className="flex flex-col md:w-8/12">
                    <Suspense fallback={<Loader />}>
                      <Outlet
                        context={{
                          agencyData,
                          validateAgency,
                        }}
                      />
                    </Suspense>
                  </div>
                </div>
              </section>
            </main>
            <Toaster />
          </div>
        </div>
      </NewGradPaidContext.Provider>
    </ThemeProvider>
  );
};
